import { FC, MutableRefObject, RefObject, useContext, useEffect, useRef, useState } from 'react';
import IconsOverlay from './IconsOverlay';
import { DocumentContext } from '@modules/documents';
import { COMMENTS_HTML_ATTRIBUTES } from '@modules/comments/constants';
import { AddMarkStep, RemoveMarkStep, ReplaceAroundStep } from '@tiptap/pm/transform';
import { Editor } from '@tiptap/core';
import { Transaction } from '@tiptap/pm/state';
import { useDebouncedCallback } from 'use-debounce';
import { useItemScreenStore } from '@modules/items';

const DEBOUNCE_DELAY = 50; // Slightly tighter feedback loop
const DEBOUNCE_MAX_WAIT = 150; // Keeps UI crisp even under heavy activity

export type TCommentIcon = {
  id: string;
  topPos: number;
  label: string;
  isStale?: boolean;
};

type TEditorCallback = ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => Editor;

type TProps = {
  refEditorContent: RefObject<HTMLDivElement>;
};

// Using Maps keyed by comment id for efficient lookups.
type SelectionElementsMap = Map<string, Element>;
type CommentIconsMap = Map<string, TCommentIcon>;
// eslint-disable-next-line no-undef
type TNodeList = NodeListOf<Element>;

/**
 * EditorCommentsContainer queries both "selection-mark" and "selection-node"
 * elements and maintains Maps for efficient updates and measurements.
 */
const EditorCommentsContainer: FC<TProps> = ({ refEditorContent }) => {
  const { editor } = useContext(DocumentContext);
  const { viewChanges } = useItemScreenStore();
  // Map to store computed comment icons; converted to an array for rendering.
  const [commentIconsMap, setCommentIconsMap] = useState<CommentIconsMap>(new Map());

  // Map to store selection elements (both marks and nodes) keyed by comment ID.
  const refSelectionElementsMap = useRef(new Map());

  const container = refEditorContent.current;

  // Debounced measurement to throttle layout recalculations.
  const debouncedMeasure = useDebouncedCallback(
    (elementsMap: SelectionElementsMap) => {
      measureElementsMap(elementsMap);
    },
    DEBOUNCE_DELAY,
    { maxWait: DEBOUNCE_MAX_WAIT }
  );

  /**
   * Queries the container for all selection-mark and selection-node elements
   * and returns them as a NodeList.
   */
  const querySelectionElements = (containerElem: HTMLElement): TNodeList => {
    return containerElem.querySelectorAll(
      `selection-mark[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}], selection-node[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}]`
    );
  };

  /**
   * Measures the elements in the map and updates state with the computed icon data.
   */
  const measureElementsMap = (elementsMap: SelectionElementsMap) => {
    if (!container) {
      setCommentIconsMap(new Map());
      return;
    }

    const newIconsMap: CommentIconsMap = new Map();
    // Check if the number of elements has changed
    let hasChanged = commentIconsMap.size !== elementsMap.size;

    elementsMap.forEach((elem, id) => {
      const icon = generateCommentIcon(elem, container, refSelectionElementsMap);
      if (icon) {
        const prevIcon = commentIconsMap.get(id);
        if (!hasChanged && (!prevIcon || prevIcon.topPos !== icon.topPos || prevIcon.isStale !== icon.isStale)) {
          hasChanged = true;
        }
        newIconsMap.set(id, icon);
      }
    });

    // Minimizes unnecessary re-renders if measurements don’t change.
    if (hasChanged) setCommentIconsMap(newIconsMap);
  };

  useEffect(() => {
    // If for some reason the editor is not available, reset the state and return.
    if (!container) {
      setCommentIconsMap(new Map());
      return;
    }

    /**
     * Handle initial content load:
     * Query for all selection elements, update the Map, and measure icon positions.
     */
    const handleOnEditorContentSet = (e: CustomEvent) => {
      const nodeList = querySelectionElements(container);
      const newMap = nodeListToMap(nodeList);
      refSelectionElementsMap.current = newMap;
      debouncedMeasure(newMap);
    };

    /**
     * Handle content updates. The following scenarios are handled:
     * 1. Adding a comment to a selection-mark
     * 2. Removing a comment from a selection-mark (after deleting a comment in the sidebar)
     * 3. Adding a comment to a selection-node
     *
     * Finally, schedule a measurement after handling all steps.
     */
    const handleOnUpdate: TEditorCallback = ({ editor: e, transaction: t }) => {
      const { steps } = t;
      if (!steps.length) {
        // If no steps, just measure
        debouncedMeasure(refSelectionElementsMap.current);
        return e;
      }

      // We only care about the first two steps
      steps.slice(0, 2).forEach((step) => {
        if (step instanceof AddMarkStep) {
          // handles adding a comment to a selection-mark
          handleAddCommentMarkStep(step);
          return;
        } else if (step instanceof RemoveMarkStep) {
          // handle removing a comment from a selection-mark after deleting a comment in the sidebar
          handleRemoveCommentMarkStep(step);
          return;
        } else if (step instanceof ReplaceAroundStep) {
          // handles adding a comment to a selection-node
          handleAddCommentNodeStep(step);
          return;
        }
      });

      // Schedule a measurement after handling all steps
      debouncedMeasure(refSelectionElementsMap.current);

      return e;
    };

    const handleAddCommentMarkStep = (step: AddMarkStep) => {
      // selection-mark only: covers both 'in progress' (NEW_COMMENT_ID) and final
      const commentId = step.mark.attrs.commentId;
      if (!commentId || !container) return;

      const selector = `selection-mark[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${commentId}"]`;
      const newElem = container.querySelector(selector);
      if (newElem) {
        refSelectionElementsMap.current.set(commentId, newElem);
      }
    };

    const handleRemoveCommentMarkStep = (step: RemoveMarkStep) => {
      // selection-mark only: comment has been completely deleted (archived)
      const commentIdRemoved = step.mark.attrs.commentId;
      if (!commentIdRemoved) return;

      refSelectionElementsMap.current.delete(commentIdRemoved);
    };

    const handleAddCommentNodeStep = (step: ReplaceAroundStep) => {
      // selection-node only: covers both 'in progress' (NEW_COMMENT_ID) and final
      const commentId = step.slice.content.firstChild?.attrs.commentId;
      if (!commentId || !container) return;

      const selector = `selection-node[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${commentId}"]`;
      const newElem = container.querySelector(selector);
      if (newElem) {
        refSelectionElementsMap.current.set(commentId, newElem);
      }
    };

    /**
     * Listen for the custom "selectionNodeRemoved" event dispatched by the NodeView.
     */
    const handleRemoveCommentNode = (e: CustomEvent) => {
      const { commentId, isStale } = e.detail;
      if (commentId) {
        if (isStale) {
          // Mark the icon as stale (might be back after a history undo)
          setCommentIconsMap((prevMap) => {
            const updatedMap = new Map(prevMap);
            const icon = updatedMap.get(commentId);
            if (icon) {
              updatedMap.set(commentId, { ...icon, isStale: true });
            }
            return updatedMap;
          });
        } else {
          // Remove the icon from selection elements Map and comment icons Map
          refSelectionElementsMap.current.delete(commentId);
          setCommentIconsMap((prevMap) => {
            const updatedMap = new Map(prevMap);
            updatedMap.delete(commentId);
            return updatedMap;
          });
        }
      }
    };

    // Attach listeners
    window.addEventListener('editorContentSet', handleOnEditorContentSet);
    window.addEventListener('selectionNodeRemoved', handleRemoveCommentNode);
    editor.on('update', handleOnUpdate);

    return () => {
      // Cleanup
      window.removeEventListener('editorContentSet', handleOnEditorContentSet);
      window.removeEventListener('selectionNodeRemoved', handleRemoveCommentNode);
      editor.off('update', handleOnUpdate);
    };
  }, []);

  /**
   * Hide the icons overlay if:
   * - we are in "view changes" mode
   * - the container is not yet available
   */
  if (viewChanges || !container) return null;

  // Convert the comment icons Map to an array for rendering.
  const commentIconsArray = Array.from(commentIconsMap.values());

  return <IconsOverlay commentIcons={commentIconsArray} />;
};

/**
 * Convert a NodeList into a Map keyed by the comment ID.
 */
const nodeListToMap = (nodeList: TNodeList): SelectionElementsMap => {
  const map = new Map<string, Element>();
  nodeList.forEach((elem) => {
    const id = elem.getAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID);
    if (id) {
      map.set(id, elem);
    }
  });
  return map;
};

/**
 * Generates a comment icon from a selection element.
 * If the element's position appears stale (e.g. rectTop is zero), re-query for a fresh reference
 * and update the Map accordingly.
 *
 * @param elem - The selection element (mark or node)
 * @param container - The container element used for relative positioning
 * @param refSelectionElementsMap - The Map of selection elements keyed by comment ID
 * @returns A TCommentIcon object or null
 */
const generateCommentIcon = (
  elem: Element,
  container: HTMLElement,
  refSelectionElementsMap: MutableRefObject<SelectionElementsMap>
): TCommentIcon | null => {
  const id = elem.getAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID);
  if (!id) return null;

  let isStale = false;

  const { rectTop, containerTop } = calculateTopPos(elem, container);
  let relativeTop = rectTop - containerTop;

  // If the element's position appears stale (rectTop is zero), get a fresh reference.
  if (rectTop === 0) {
    const freshElem = container.querySelector(`[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${id}"]`);
    if (freshElem) {
      const { rectTop: newRectTop, containerTop: newContainerTop } = calculateTopPos(freshElem, container);
      relativeTop = newRectTop - newContainerTop;
      // Update the Map with the fresh reference.
      refSelectionElementsMap.current.set(id, freshElem);
    } else {
      /**
       * If the element is missing, mark it as stale. This can happen when the element is removed
       * from the DOM (e.g. when a comment is deleted) or when the element is not yet rendered (unlikely).
       * However, we wanna keep the element in the Map in case the user undoes the deletion (via ctrl+z).
       * The only disadvantage is that we'll consequently query for the element on every re-measurement cycle.
       */
      isStale = true;
    }
  }
  return {
    id,
    topPos: Math.round(relativeTop),
    label: formatCommentLabel(elem.textContent),
    isStale,
  };
};

/**
 * Calculates the top offset of an element relative to its container.
 *
 * @param elem - The element to measure
 * @param container - The container element
 * @returns An object with the element’s top (rectTop) and container’s top (containerTop)
 */
const calculateTopPos = (elem: Element, container: HTMLElement): { rectTop: number; containerTop: number } => {
  const rect = elem.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return { rectTop: rect.top, containerTop: containerRect.top };
};

/**
 * Truncates text content to create a small preview label.
 *
 * @param text - The full text content
 * @returns A truncated label string
 */
const formatCommentLabel = (text: string | null): string => {
  const snippet = (text || '').trim().slice(0, 10);
  return `... ${snippet} ...`;
};

export default EditorCommentsContainer;
